import React, { useMemo, useRef } from 'react';
import { useAppSelector } from 'hooks';
import ReactSelect, { components as rsComponents, MenuListProps } from 'react-select';

import { InputProps } from '../props';

interface ComponentProps extends InputProps {
  ignorePlaceholder?: boolean;
  // Opt-in only (default off). MultiSelect backs the generic 'multi-lookup' field type used by
  // ~30 search/filter forms app-wide via Field.tsx/SearchField.tsx, so this must not change
  // default behaviour for every other consumer - only call sites that explicitly ask for it get
  // the "select all currently filtered options" affordance. See TAV-3250.
  allowSelectAll?: boolean;
  /** Visible label for the control. Also what names it to assistive technology. */
  label?: string;
}

interface Option {
  value: string | number;
  label: string;
}

type SelectAllHandler = (filteredOptions: Option[]) => void;

/**
 * Builds the "Select All" MenuList override for one MultiSelect instance.
 *
 * This lives at module scope, and is called at most once per instance, so the MenuList component
 * TYPE reference stays stable for the life of that instance. Declaring the component inside the
 * render body (or memoising it against a handler that closes over `value`) hands React a brand new
 * component type on each render, which unmounts and remounts the entire menu subtree and throws
 * away the option list scroll position. That is not a rare edge case here: `closeMenuOnSelect` is
 * false, so the menu deliberately stays open while the user picks, and every pick calls
 * setFieldValue, re-renders the parent form and re-renders this component. The live handler is
 * reached through a ref, so the closure always sees the current value/setFieldValue/id while the
 * ref object itself, and therefore the component identity, never changes. See TAV-3250.
 */
const createSelectAllMenuList = (handlerRef: React.MutableRefObject<SelectAllHandler>) => {
  // Reads the options react-select has already filtered down to (via its own built-in search box)
  // straight off the rendered Option elements, rather than re-implementing react-select's own
  // filter matching - so "Select All" always matches exactly what's currently visible in the menu.
  const SelectAllMenuList = (menuListProps: MenuListProps<Option, true>) => {
    const filteredOptions = React.Children.toArray(menuListProps.children)
      .map((child) => (React.isValidElement(child) ? (child.props as { data?: Option }).data : undefined))
      .filter((data): data is Option => !!data);

    return (
      <rsComponents.MenuList {...menuListProps}>
        {filteredOptions.length > 0 && (
          /*
           * Known limitation (TAV-3275): this row is mouse-activated only.
           *
           * It is a plain div injected above react-select's own options, not one of react-select's
           * `Option` components, so it never joins react-select's internal focused-option state and
           * never receives `aria-activedescendant` from the menu's built-in arrow-key navigation. A
           * keyboard-only user arrowing through the dropdown can therefore never land on it or
           * activate it; only a mouse click works.
           *
           * This degrades safely rather than blocking anyone: keyboard users simply select options
           * individually, which is exactly the pre-TAV-3250 behaviour. TAV-3275 tracks the proper
           * fix, reworking Select All into a real react-select sentinel option so it inherits
           * arrow-key navigation, `aria-activedescendant` and Enter activation. That is a refactor
           * of a shared component backing ~30 forms, hence a follow-up rather than part of this
           * change.
           *
           * The jest-axe audit in MultiSelect.test.tsx does not catch this, because the div sits
           * inside a valid `role="listbox"` and so is structurally legal, just unreachable.
           *
           * Deliberately not partially fixed: an `onKeyDown` handler here would never fire (the
           * element never takes DOM focus), and raising `tabIndex` above -1 would not help either,
           * because Tab inside an open react-select menu selects and closes rather than moving
           * focus into the option list.
           */
          <div
            role="option"
            aria-selected={false}
            tabIndex={-1}
            className="form-input-multi-select__select-all"
            data-cy="multi-select-select-all-option"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => handlerRef.current(filteredOptions)}
          >
            Select All ({filteredOptions.length})
          </div>
        )}
        {menuListProps.children}
      </rsComponents.MenuList>
    );
  };

  SelectAllMenuList.displayName = 'SelectAllMenuList';

  return SelectAllMenuList;
};

const MultiSelect: React.FC<ComponentProps> = ({
  id,
  label,
  value,
  placeholder,
  ignorePlaceholder = false,
  disabled = false,
  options,
  selectorOptions,
  setFieldValue,
  className,
  allowSelectAll = false,
}) => {
  let selectOptions: Option[] = [];
  if (options) {
    selectOptions = options.map(({ id, name }) => ({ value: id, label: name }));
  } else if (selectorOptions) {
    const rows = useAppSelector(selectorOptions);
    selectOptions = rows.map(({ id, name }) => ({ value: id, label: name }));
  }

  // Reassigned on every render so the MenuList always invokes the current closure (latest value,
  // setFieldValue and id), while the ref object itself stays the same for the life of the instance.
  const handleSelectAllFilteredRef = useRef<SelectAllHandler>(() => undefined);
  handleSelectAllFilteredRef.current = (filteredOptions: Option[]) => {
    if (!setFieldValue) return;
    const filteredValues = filteredOptions.map((option) => option.value);
    const currentValues: Array<string | number> = value ?? [];
    setFieldValue(id, Array.from(new Set([...currentValues, ...filteredValues])));
  };

  // Keyed on allowSelectAll alone (never on value), so both this object and the MenuList component
  // type inside it are created at most once per instance and react-select sees a stable override.
  const selectComponents = useMemo(
    () => (allowSelectAll ? { MenuList: createSelectAllMenuList(handleSelectAllFilteredRef) } : undefined),
    [allowSelectAll],
  );

  // react-select renders its own inner input with a generated id, so `htmlFor={id}` would point at
  // the wrapper and leave the control itself unlabelled. `inputId` is what makes the association
  // real, and the aria-label covers a caller that gives no visible label.
  const inputId = `${id}-input`;

  return (
    <>
      {label && (
        <label className="form-label" htmlFor={inputId}>
          {label}
        </label>
      )}
      <ReactSelect
        isMulti
        closeMenuOnSelect={false}
        id={id}
        inputId={inputId}
        aria-label={label ?? placeholder}
        name={id}
        value={selectOptions.filter((option) => value?.includes(option.value))}
        isDisabled={disabled}
        options={selectOptions}
        className={className}
        classNamePrefix={'form-input-multi-select'}
        components={selectComponents}
        onChange={(options) =>
          setFieldValue &&
          setFieldValue(
            id,
            options.map((option) => option.value),
          )
        }
        placeholder={!ignorePlaceholder ? placeholder : undefined}
      />
    </>
  );
};

export default MultiSelect;
